import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, Minus, Clock, CreditCard, Sparkles, Star, Info, Zap, Globe, Target, Users, BarChart3, Activity, RefreshCw, Download, FileText, Award, QrCode, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// UN SDG Icons (simplified)
const SDG_ICONS = {
  8: { name: "Decent Work", color: "#a21942" },
  9: { name: "Industry & Innovation", color: "#fd6925" },
  10: { name: "Reduced Inequalities", color: "#dd1367" },
  11: { name: "Sustainable Cities", color: "#fd9d24" },
  16: { name: "Peace & Justice", color: "#00689d" },
  17: { name: "Partnerships", color: "#19486a" }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const getTransactionIcon = (txType) => {
  switch (txType) {
    case 'credit': return <Plus className="h-4 w-4 text-green-600" />;
    case 'bonus': return <Star className="h-4 w-4 text-yellow-600" />;
    case 'debit': return <Minus className="h-4 w-4 text-red-600" />;
    case 'refund': return <RefreshCw className="h-4 w-4 text-blue-600" />;
    default: return <Activity className="h-4 w-4 text-gray-600" />;
  }
};

export default function B2BWalletTabs({ selectedTab, services, ledgerData, packages, invoices, impactData, labelData, tenantId, createOrder }) {
  return (
    <>
      {selectedTab === 'overview' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-indigo-600" />
              Service Costs (in Service Credits)
            </CardTitle>
            <CardDescription>
              Each service consumes a specific amount of SC from your wallet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Activity className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{service.description}</p>
                      <p className="text-xs text-gray-500">{service.service}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-indigo-700 text-white text-lg px-3 font-semibold">
                      {service.cost_sc} SC
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedTab === 'ledger' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  Transaction Ledger
                </CardTitle>
                <CardDescription>
                  {ledgerData?.total_count || 0} transactions • 
                  Credits: {ledgerData?.period_credits || 0} SC • 
                  Debits: {ledgerData?.period_debits || 0} SC
                </CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {ledgerData?.transactions?.length > 0 ? (
              <div className="space-y-2">
                {ledgerData.transactions.map((tx, idx) => (
                  <div
                    key={tx.id || idx}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${
                        tx.tx_type === 'credit' || tx.tx_type === 'bonus'
                          ? 'bg-green-100'
                          : 'bg-red-100'
                      }`}>
                        {getTransactionIcon(tx.tx_type)}
                      </div>
                      <div>
                        <p className="font-medium">
                          {tx.description || tx.service || tx.tx_type}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="h-3 w-3" />
                          {formatDate(tx.created_at)}
                          {tx.ref_id && (
                            <span className="text-indigo-600">• Ref: {tx.ref_id.slice(0, 8)}...</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${
                        tx.tx_type === 'credit' || tx.tx_type === 'bonus'
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {tx.tx_type === 'credit' || tx.tx_type === 'bonus' ? '+' : '-'}
                        {tx.amount_sc} SC
                      </p>
                      <p className="text-xs text-gray-500">
                        Balance: {tx.balance_after} SC
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No transactions yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 'packages' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              Credit Packages
            </CardTitle>
            <CardDescription>
              Purchase Service Credits with solidarity bonuses
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg, idx) => {
                const hasBonus = pkg.bonus_percent > 0;
                return (
                  <Card
                    key={pkg.id || idx}
                    className={`relative overflow-hidden transition-all hover:shadow-xl ${
                      hasBonus ? 'border-2 border-indigo-300' : ''
                    }`}
                  >
                    {hasBonus && (
                      <div className="absolute top-0 right-0 bg-gradient-to-l from-indigo-600 to-blue-600 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                        +{pkg.bonus_percent}% BONUS
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center mb-2">
                        <Sparkles className="h-8 w-8 text-indigo-600" />
                      </div>
                      <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-indigo-600">
                          {pkg.sc_amount.toLocaleString()}
                        </p>
                        <p className="text-gray-500 text-sm">Service Credits</p>
                        {hasBonus && (
                          <p className="text-green-600 text-sm font-medium mt-1">
                            + {pkg.bonus_sc.toLocaleString()} bonus SC
                          </p>
                        )}
                      </div>
                      <div className="text-center py-3 bg-gray-50 rounded-lg">
                        <p className="text-3xl font-bold">€{pkg.price_eur.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">
                          €{(pkg.price_eur / (pkg.sc_amount + pkg.bonus_sc)).toFixed(3)} / SC
                        </p>
                      </div>
                      <Button
                        className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                        onClick={() => createOrder(`SC_${pkg.name.toUpperCase().replace(' ', '_')}_${pkg.sc_amount}`)}
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Purchase
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedTab === 'invoices' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  Invoices & Orders
                </CardTitle>
                <CardDescription>
                  {invoices.length} invoices
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {invoices.length > 0 ? (
              <div className="space-y-3">
                {invoices.map((inv, idx) => (
                  <div
                    key={inv.order_id || idx}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${
                        inv.status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        <FileText className={`h-4 w-4 ${
                          inv.status === 'paid' ? 'text-green-600' : 'text-yellow-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium">{inv.invoice_number || inv.order_id}</p>
                        <p className="text-xs text-gray-500">
                          {inv.pack_name} • {formatDate(inv.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={
                        inv.status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }>
                        {inv.status?.toUpperCase()}
                      </Badge>
                      <p className="font-bold">{inv.amount_due?.toLocaleString()} {inv.currency}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${BACKEND_URL}${inv.pdf_url}`, '_blank')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No invoices yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 'impact' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-indigo-600" />
                ESS Impact & UN SDG Contributions
              </CardTitle>
              <CardDescription>
                Your usage contributes to sustainable development goals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg text-center">
                  <BarChart3 className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-indigo-700">
                    {impactData?.impact?.total_sc_consumed?.toFixed(0) || 0}
                  </p>
                  <p className="text-gray-600 text-sm">Total SC Consumed</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg text-center">
                  <Activity className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-green-700">
                    {impactData?.impact?.simulations_count || 0}
                  </p>
                  <p className="text-gray-600 text-sm">Simulations Run</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg text-center">
                  <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-purple-700">
                    {impactData?.impact?.pdfs_generated || 0}
                  </p>
                  <p className="text-gray-600 text-sm">Reports Generated</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  UN Sustainable Development Goals Contributions
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {impactData?.sdg_contributions && Object.entries(impactData.sdg_contributions).map(([sdgNum, data]) => (
                    <div
                      key={sdgNum}
                      className="p-4 rounded-lg text-center text-white"
                      style={{ backgroundColor: SDG_ICONS[sdgNum]?.color || '#666' }}
                    >
                      <p className="text-3xl font-bold">SDG {sdgNum}</p>
                      <p className="text-sm opacity-90 mb-2">{SDG_ICONS[sdgNum]?.name}</p>
                      <p className="text-xl font-semibold">{data.contribution_sc?.toFixed(1)} SC</p>
                    </div>
                  ))}
                </div>
              </div>

              {impactData?.solidarity_contribution && (
                <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-full">
                      <Star className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-900">Solidarity Contribution</h4>
                      <p className="text-amber-700 text-sm mt-1">
                        {impactData.solidarity_contribution.percent}% of your consumption 
                        ({impactData.solidarity_contribution.amount_sc?.toFixed(2)} SC) 
                        contributes to platform accessibility and ESS infrastructure.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="py-4">
              <p className="text-xs text-gray-600 text-center">
                <Info className="h-3 w-3 inline mr-1" />
                {impactData?.disclaimer || 'Service Credits (SC) are units of service consumption. They do not constitute currency, financial instruments, or investments.'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {selectedTab === 'label' && (
        <div className="space-y-6">
          {labelData?.status === 'active' ? (
            <>
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 rounded-full">
                        <Award className="h-8 w-8 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-green-800">{labelData.label}</CardTitle>
                        <CardDescription className="text-green-600">
                          {labelData.tenant_name}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge className="bg-green-700 text-white text-lg px-4 py-2 font-semibold">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {labelData.status?.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-500">Level</p>
                      <p className="font-semibold capitalize">{labelData.level}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-500">Issued</p>
                      <p className="font-semibold">{labelData.issued_at}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-500">Expires</p>
                      <p className="font-semibold">{labelData.expires_at}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-xs text-gray-500">Signature</p>
                      <p className="font-semibold font-mono">{labelData.signature_short}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">UN SDGs Covered</p>
                    <div className="flex flex-wrap gap-2">
                      {labelData.odds?.map(sdg => (
                        <span
                          key={sdg}
                          className="px-3 py-1 rounded-full text-white text-sm font-medium"
                          style={{ backgroundColor: SDG_ICONS[sdg]?.color || '#666' }}
                        >
                          SDG {sdg}: {SDG_ICONS[sdg]?.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => window.open(labelData.verification_url, '_blank')}
                    >
                      <Globe className="h-4 w-4 mr-2" />
                      View Public Page
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(labelData.hash);
                        toast.success('Hash copied!');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Hash
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    Embed Widget on Your Website
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="p-4 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto">
{`<div id="ess-verify-widget"
     data-tenant="${tenantId}"
     data-lang="en"
     data-theme="light"
     data-size="standard">
</div>
<script src="${BACKEND_URL}/api/widget/verify-ess-partner.js" async defer></script>`}
                  </pre>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      navigator.clipboard.writeText(`<div id="ess-verify-widget" data-tenant="${tenantId}" data-lang="en" data-theme="light" data-size="standard"></div><script src="${BACKEND_URL}/api/widget/verify-ess-partner.js" async defer></script>`);
                      toast.success('Widget code copied!');
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Code
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-gray-50">
              <CardContent className="py-12 text-center">
                <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No ESS Label Yet</h3>
                <p className="text-gray-500 mb-4">
                  Your partner account is not yet labeled. Contact administration to request a label.
                </p>
                <Badge className="bg-gray-700 text-white font-semibold">
                  {labelData?.status || 'not_found'}
                </Badge>
              </CardContent>
            </Card>
          )}

          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="py-4">
              <p className="text-xs text-gray-600 text-center">
                <Info className="h-3 w-3 inline mr-1" />
                {labelData?.disclaimer || 'This label certifies alignment with ESS principles and UN SDGs. It does not constitute a financial certification or regulatory approval.'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
